import React from "react";
import {useSelector} from "react-redux";
import {Backdrop, Button as MuiButton, Grid, Typography} from "@material-ui/core";
import {Formik, FormikValues, useFormikContext} from "formik";
import numeral from "numeral";
import * as yup from "yup";
import {RootState} from "../../../../../reducers";
import {useActions} from "../../../../../actions";
import * as EstimationActions from "../../../../../actions/estimation";
import * as EstimatorActions from "../../../../../actions/estimator";
import {AccountStatus} from "../../../../../model";
import InvitationAllocations from "../../../../../components/client/InvitationAllocations";
import {createEstimateReports} from "../../../../../components/client/Proposal";
import TestingProcessDialog from "../../../../../components/client/Estimation/TestingProcessDialog";
import {
    submitEstimationFormik,
    SubmitEstimationFormikProps
} from "../../../Estimation/V1/Components/EstimationFormikHandler";
import {useCreateAllocationPayLoad} from "../../../Estimation/Hooks/Hooks";
import EstimationFormContext from "../../../Estimation/V1/Context/EstimationFormContext";
import Socket from "../../../Estimation/V1/Socket";
import StepOne from "../../../Estimation/V1/Components/StepOne";
import StepThree from "../../../Estimation/V1/Components/StepThree";
import StepTwo from "../../../Estimation/V1/Components/StepTwo";
import {ANNUALLY} from "../../../Estimation/V1/Components/ContributionFrequency";
import {isDebug, joinAsFullName, maskCurrency, noop} from "../../../../../utils/commonUtils";
import {CallingFromObject, NationalLifeCarrierDetail, REQUIRED_FIELD} from "../../../../../constants";
import FormikTokenUpdate from "../../../Estimation/V1/Components/FormikTokenUpdate";


interface IllustrationsChildFormProps {
    callingFrom: string;
    carrierId: number;
    disableRunButton: boolean;
    estimationProviderContextValue: any;
    strategyId: number;
    clientDetails: any;
}

const IllustrationsChildForm = (childFormProps: IllustrationsChildFormProps) => {
    const {
        callingFrom,
        carrierId,
        disableRunButton,
        estimationProviderContextValue,
        strategyId,
        clientDetails,
    } = childFormProps;
    const {errors, handleSubmit, initialValues, values} = useFormikContext();
    const {getMinimumContribution} = useActions(EstimationActions);
    const getMinContribution = async (query: any) => {
        await getMinimumContribution(query);
    }
    return (
            <form onSubmit={handleSubmit}>
                <EstimationFormContext.Provider value={estimationProviderContextValue}>
                    <FormikTokenUpdate getMinContribution={getMinContribution} />
                    <StepOne callingFrom={callingFrom}/>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <StepTwo callingFrom={callingFrom}/>
                        </Grid>

                        <Grid item xs={12}>
                            <StepThree callingFrom={callingFrom}/>
                        </Grid>

                        <Grid item xs={12}>
                            <InvitationAllocations callingFrom={callingFrom} userDetails={{ carrierId, strategyId }}
                                                   clientDetails={clientDetails} />
                        </Grid>

                        <span className="hLine"/>
                        <Grid item xs={12}>
                            <MuiButton
                                    className="floatRight"
                                    color="primary"
                                    disabled={disableRunButton}
                                    size="medium"
                                    type="submit"
                                    variant="contained"
                            >
                                Run Illustration
                            </MuiButton>
                        </Grid>

                        {
                                disableRunButton &&
                                <Grid item xs={12}>
                                    <Typography align="right" className="colorRed w100" variant="body1">
                                        A Kaizen admin must run the final illustration.
                                    </Typography>
                                </Grid>
                        }

                        {
                                isDebug() &&
                                <Grid item xs={12}>
                                    <pre>
                                        {JSON.stringify({errors, initialValues, values}, null, 2)}
                                    </pre>
                                </Grid>
                        }
                    </Grid>
                </EstimationFormContext.Provider>
            </form>
    );
};

export const Illustrations = ({setForceRefreshSummary}: { setForceRefreshSummary: Function }) => {
    const estimationActions = useActions(EstimationActions);
    const estimatorActions = useActions(EstimatorActions);

    const [issueAge, setIssueAge] = React.useState<null | string>(null);
    const [requestId, setRequestId] = React.useState<null | string>(null);
    const [isEstimationDataLoading, setIsEstimationDataLoading] = React.useState<boolean>(false);
    const [estimatorData, setEstimatorData] = React.useState<any>(null);
    const [isReportRequestAutomated, setIsReportRequestAutomated] = React.useState<boolean>(false);

    const callingFrom: string = CallingFromObject.AGENT_ADMIN_ILLUSTRATION;
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;
    const {user} = useSelector((state: RootState) => state.auth);
    const clientDetails = useSelector((state: RootState) => state.clientAccount) || {};
    const client: any = clientDetails && clientDetails.client;
    const clientPolicy: any = client && client.clientPolicies && client.clientPolicies[0];
    const carrierId = (clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id) || NationalLifeCarrierDetail.id;
    const strategyId = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id;
    const estimationProviderContextValue = {issueAge, setIssueAge};
    const invStats = (clientDetails && clientDetails.inviteStatus) as number;
    const disableRunButton = (invStats >= AccountStatus.ApplicationSubmittedToCarrier);
    const calculateIssueAge = async (dob: string, carrierId: number) => {
        const response: any = await estimationActions.getIssueAge(dob, carrierId);
        isDebug() && console.info({response});
        setIssueAge(response && response.issueAge);
    };
    const onClearEstimationData = async () => {
        await estimationActions.clearEstimation(callingFrom, true);
    };
    const createAllocationPayLoad = useCreateAllocationPayLoad(estimatorData && estimatorData.id);
    const initialValues = {
        carrierId,
        contributionFrequency: ANNUALLY,
        dob: client && client.dob,
        firstName: clientDetails.firstName,
        gender: client && client.gender,
        healthType: clientPolicy && clientPolicy.healthType,
        InvestmentType: client && client.clientInvestmentType,
        issueAge: (clientPolicy && clientPolicy.issueAge) || issueAge,
        lastName: clientDetails.lastName,
    };
    const socketButtonClick = (loader: any) => {
        const userObj = {
            clientPolicy,
            companyLinkUrl: clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl,
            id: clientDetails && clientDetails.id,
            loggerInUserId: user.id,
            loggerInUserRole: user.role,
            role: clientDetails && clientDetails.role as number,
        };
        const payload: any = {
            callingFrom,
            estimationActions,
            initRequest: () => {
                if (loader) {
                    setIsReportRequestAutomated(false);
                } else {
                    setIsReportRequestAutomated(true);
                }
            },
            onReportsAlreadyExist: () => setIsEstimationDataLoading(false),
            onRequestIdCreated: (id: any) => setRequestId(id),
            user: userObj
        };
        createEstimateReports(payload).then();
    };
    const onSubmit = async (values: FormikValues) => {
        // clear
        await onClearEstimationData();

        // run new illustration
        const payload: SubmitEstimationFormikProps = {
            agentName: joinAsFullName(clientPolicy.agent.user),
            atlantic: false,
            callingFrom,
            clientPolicy,
            createAllocationPayLoad,
            estimationActions,
            estimatorData,
            issueAge: clientPolicy.issueAge || issueAge,
            loggedInUser: user,
            role: clientDetails.role,
            setIsEstimationDataLoading,
            setRequestId,
            user: clientDetails,
            userId: clientDetails.id,
            userName: joinAsFullName(clientDetails),
            values,
        };
        submitEstimationFormik(payload).then();
    };

    const commonBuilder: yup.WhenOptions<any> = {
        otherwise: yup.string().notRequired(),
        then: yup.string().required(REQUIRED_FIELD),
    };
    const validateObject = {
        annualContribution: yup.string().when("primaryFocusPlan", {
            ...commonBuilder,
            is: "contributionAmount",
            then: yup.string().required(REQUIRED_FIELD).test("min-contribution", `Min Amount should be ${maskCurrency(minCon + '')}`, value => {
                if (!value) {
                    return false;
                }
                const numValue = numeral(value).value();
                return numValue >= minCon;
            })
        }).nullable(),
        deathBenefitCalculationOptions: yup.string().when("primaryFocusPlan", {
            ...commonBuilder,
            is: "deathBenefit",
            then: yup.string().oneOf(["amountToContribute", "lifeTimeDeathBenefit"], "Not Valid").required(REQUIRED_FIELD),
        }).nullable(),
        desireLifeTimeBenefits: yup.string().when("deathBenefitCalculationOptions", {
            ...commonBuilder,
            is: "lifeTimeDeathBenefit"
        }),
        dob: yup.string().required(REQUIRED_FIELD).nullable(),
        gender: yup.number().required(REQUIRED_FIELD).nullable(),
        healthType: yup.string().required(REQUIRED_FIELD).nullable(),
        InvestmentType: yup.string().required(REQUIRED_FIELD).nullable(),
        primaryFocusPlan: yup.string().oneOf(["deathBenefit", "contributionAmount"], "Not Valid").required(REQUIRED_FIELD).nullable(),
        totalContribution: yup.string().when("deathBenefitCalculationOptions", {
            ...commonBuilder,
            is: "amountToContribute",
        }).nullable(),
    };
    const validationSchema = yup.object(validateObject);

    React.useEffect(() => {
        (async () => {
            if (!isEstimationDataLoading) {
                setForceRefreshSummary(Math.random());
            }
        })();
    }, [isEstimationDataLoading]);

    React.useEffect(() => {
        (async () => {
            if (carrierId && strategyId) {
                const dob = client && client.dob;
                await calculateIssueAge(dob, carrierId);
                const res = await estimatorActions.getEstimatorDetails({carrierId: carrierId, strategyId: strategyId});
                if (!!res) {
                    setEstimatorData(res);
                }
            }
        })();
    }, [carrierId, strategyId]);


    return (
            <>
                <Socket
                        callingFrom={callingFrom}
                        closeAfterFullProcess={true}
                        handleButtonClick={socketButtonClick}
                        handleReset={noop}
                        handleResult={noop}
                        isReportRequestAutomated={isReportRequestAutomated}
                        page={"estimator"}
                        requestId={requestId}
                        setIsEstimationDataLoading={setIsEstimationDataLoading}
                        setIsReportDataLoading={noop}
                        setIsReportRequestCompleted={noop}
                        setLifeDeathBenefitSeeking={noop}
                        setResponseData={noop}
                        userId={client && client.userId}
                />


                <Formik
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        validationSchema={validationSchema}
                >
                    <IllustrationsChildForm
                            callingFrom={callingFrom}
                            carrierId={carrierId}
                            disableRunButton={disableRunButton}
                            estimationProviderContextValue={estimationProviderContextValue}
                            strategyId={strategyId}
                            clientDetails={clientDetails}
                    />
                </Formik>

                {
                        isEstimationDataLoading &&
                        <Backdrop className={"classes.backdrop"} open={isEstimationDataLoading}>
                            <TestingProcessDialog
                                    open={isEstimationDataLoading}
                                    isEstimationDataLoading={isEstimationDataLoading}
                                    handleClose={() => setIsEstimationDataLoading(false)}
                                    getEstimationProgress={estimationActions.getEstimationProgress}
                            />
                        </Backdrop>
                }
            </>
    );
};

export default Illustrations;
